import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useConfig, useTenantConfig, useWorkshopServiceAdvisors } from "./api";
import { useAuth } from "./AuthContext";
import { identityControlFor } from "./access";

const DemoIdentityContext = createContext(null);

function defaultLocationsFor(role, workshops) {
  if (!workshops.length) return [];
  if (role === "master") return workshops;
  if (role === "gm") return workshops.slice(0, 2);
  return [workshops[0]];
}

export function DemoIdentityProvider({ children }) {
  // Workshops and advisors are real data — the same /config and
  // /workshop-service-advisors endpoints Overview and Stakeholders already use.
  // There is no seed/demo data behind this switcher anymore.
  const { data: cfgData } = useConfig();
  const { data: advisorsData } = useWorkshopServiceAdvisors();

  const workshops = useMemo(
    () => [...new Set((cfgData?.rows || []).map((r) => r["Workshop Location"]).filter(Boolean))],
    [cfgData]
  );
  const advisorRows = useMemo(() => advisorsData?.rows || [], [advisorsData]);
  const advisorsForWorkshop = useCallback(
    (workshop) => advisorRows.filter((a) => a.workshop_location === workshop),
    [advisorRows]
  );

  const { me } = useAuth();

  const [role, setRoleState] = useState("master");
  const [locations, setLocationsState] = useState([]);
  const [advisorName, setAdvisorNameState] = useState(null);

  const defaultAdvisorFor = useCallback(
    (r, locs) => {
      if (r !== "sa" || !locs[0]) return null;
      return advisorsForWorkshop(locs[0])[0]?.name || null;
    },
    [advisorsForWorkshop]
  );

  const workshopsRef = useRef(workshops);
  workshopsRef.current = workshops;

  // Only a real master account may "view as" another role via the switcher — everyone
  // else is locked to their own real role and their own assigned workshop(s), so the
  // dashboard never falls back to the "master" default state/locations for a non-master
  // login. me.workshop_locations is server-resolved (GM: every workshop with them as GM;
  // everyone else: their single workshop_location).
  useEffect(() => {
    if (!me?.role || me.role === "master") return;
    setRoleState(me.role);
    const assigned = (me.workshop_locations || []).filter(Boolean);
    setLocationsState(assigned.length ? assigned : defaultLocationsFor(me.role, workshopsRef.current));
  }, [me?.role, me?.workshop_locations]);

  // Changing role resets location to that role's default rather than carrying over a stale one.
  const setRole = useCallback(
    (nextRole) => {
      setRoleState(nextRole);
      const nextLocations = defaultLocationsFor(nextRole, workshopsRef.current);
      setLocationsState(nextLocations);
      setAdvisorNameState(defaultAdvisorFor(nextRole, nextLocations));
    },
    [defaultAdvisorFor]
  );

  // Changing location resets the advisor — an SA's own scorecard is scoped to one workshop.
  const setLocations = useCallback(
    (nextLocations) => {
      setLocationsState(nextLocations);
      setAdvisorNameState(defaultAdvisorFor(role, nextLocations));
    },
    [role, defaultAdvisorFor]
  );

  const toggleLocation = useCallback(
    (workshop) => {
      setLocations(
        locations.includes(workshop) ? locations.filter((l) => l !== workshop) : [...locations, workshop]
      );
    },
    [locations, setLocations]
  );

  const setAdvisorName = useCallback((name) => setAdvisorNameState(name), []);

  // /config loads asynchronously — locations starts empty and fills in once the real
  // workshop list arrives, using whatever role is currently selected.
  useEffect(() => {
    if (!workshops.length) return;
    setLocationsState((prev) => (prev.length ? prev : defaultLocationsFor(role, workshops)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshops]);

  // Sits outside TenantConfigProvider, so it reads the shared query directly.
  const { data: tenantConfig } = useTenantConfig(!!me);
  const policy = (tenantConfig?.roles || []).find((r) => r.code === role)?.access_policy;
  const control = role === "master" ? identityControlFor("entire_brand", role) : identityControlFor(policy, role);

  const value = useMemo(
    () => ({
      role,
      setRole,
      locations,
      setLocations,
      toggleLocation,
      advisorName,
      setAdvisorName,
      control,
      workshops,
      advisorsForWorkshop,
      // Effective scope: 'all' | array of workshop names
      scopeLocations: role === "master" ? "all" : locations,
    }),
    [role, setRole, locations, setLocations, toggleLocation, advisorName, setAdvisorName, control, workshops, advisorsForWorkshop]
  );

  return <DemoIdentityContext.Provider value={value}>{children}</DemoIdentityContext.Provider>;
}

export function useDemoIdentity() {
  const ctx = useContext(DemoIdentityContext);
  if (!ctx) throw new Error("useDemoIdentity must be used within DemoIdentityProvider");
  return ctx;
}
